import { useCallback } from 'react';
import { SprintAPI } from '../../utils/api';
import { filterDependencies } from '../../components/SprintDetail/hooks/taskActions/utils';
import {
  hasConditionForTask,
  removeTaskFromConditions,
} from '../../components/SprintDetail/shared/ConditionsDropdown/utils/conditionHelpers';
import { dispatchTasksUpdatedEvent, dispatchCancelSprintSyncDebounce } from './utils';
import { persistMovedMissionPair } from './persistMovedMissionPair';
import { calculateNextNodePosition } from '../../utils/nodePositioning';
import { recordUndoableAction } from '../../utils/undoRecordBridge';
import { getMissionDragContext, getMissionToSidebarState } from '../../utils/missionToSidebarBridge';
import { holdSprintSaves, releaseSprintSaves } from '../../utils/sprintSaveHold';
import { isDynamoItemSizeError } from '../../utils/sprintItemSize';
import { showToast } from '../../components/Toast';

const isTemplateSprint = (sprint) =>
  sprint?.isTemplate === true || sprint?.type === 'template';

const buildMoveUpdates = (sourceSprint, targetSprint, taskId) => {
  const task = (sourceSprint.tasks || []).find((t) => t.id === taskId);
  if (!task || task.nestedSprintId) {
    console.warn('[M2S] buildMoveUpdates rejected —', !task
      ? `task ${taskId} not found in source sprint ${sourceSprint?.id} (${sourceSprint?.tasks?.length ?? 'no'} tasks)`
      : 'task is a nested-campaign link, not a regular mission');
    return null;
  }
  if (isTemplateSprint(targetSprint) || targetSprint.deletedAt) {
    console.warn('[M2S] buildMoveUpdates rejected — target is a template or deleted', {
      targetId: targetSprint?.id,
      isTemplate: isTemplateSprint(targetSprint),
      deletedAt: targetSprint?.deletedAt,
    });
    return null;
  }
  if ((targetSprint.tasks || []).some((t) => t.id === taskId)) {
    console.warn('[M2S] buildMoveUpdates rejected — target already contains this task', {
      taskId,
      targetId: targetSprint?.id,
    });
    return null;
  }

  const filteredSourceTasks = (sourceSprint.tasks || [])
    .filter((t) => t.id !== taskId)
    .map((t) => ({
      ...t,
      dependsOn: filterDependencies(t.dependsOn, taskId),
      unlockConditions:
        t.unlockConditions && hasConditionForTask(t.unlockConditions, taskId)
          ? removeTaskFromConditions(t.unlockConditions, taskId)
          : t.unlockConditions,
    }));

  const sourcePositions = { ...(sourceSprint.nodePositions || {}) };
  delete sourcePositions[taskId];

  const existingTargetTasks = targetSprint.tasks || [];
  const existingTargetPositions = targetSprint.nodePositions || {};
  const newPosition = calculateNextNodePosition(existingTargetTasks, existingTargetPositions);

  const movedTask = {
    ...task,
    x: newPosition.x,
    y: newPosition.y,
  };

  const targetPositions = {
    ...existingTargetPositions,
    [taskId]: newPosition,
  };

  return {
    previousSource: sourceSprint,
    previousTarget: targetSprint,
    updatedSource: {
      ...sourceSprint,
      tasks: filteredSourceTasks,
      nodePositions: sourcePositions,
    },
    updatedTarget: {
      ...targetSprint,
      tasks: [...existingTargetTasks, movedTask],
      nodePositions: targetPositions,
    },
  };
};

/** Resolve source sprint with full tasks (prefer live view over stale global metadata). */
export function resolveSourceSprintForMove(sourceSprintId, taskId, inMemorySprints = []) {
  const global = inMemorySprints.find((s) => s.id === sourceSprintId);
  const ctx = getMissionDragContext();
  const localSprint = ctx?.localSprint;
  const isMultiCampaign = ctx?.isMultiCampaignMode;

  const bridge = getMissionToSidebarState();
  const snapshotTask =
    bridge.sourceTask?.id === taskId && bridge.sourceSprintId === sourceSprintId
      ? bridge.sourceTask
      : null;

  const withSnapshotTask = (sprintShell) => {
    if (!snapshotTask || !sprintShell) return null;
    const tasks = Array.isArray(sprintShell.tasks) ? [...sprintShell.tasks] : [];
    const idx = tasks.findIndex((t) => t.id === taskId);
    if (idx >= 0) {
      tasks[idx] = snapshotTask;
    } else {
      tasks.push(snapshotTask);
    }
    return {
      ...sprintShell,
      id: sourceSprintId,
      tasks,
      nodePositions:
        bridge.sourceNodePositions
        ?? sprintShell.nodePositions
        ?? {},
    };
  };

  if (!isMultiCampaign && localSprint?.id === sourceSprintId && Array.isArray(localSprint.tasks)) {
    if (localSprint.tasks.some((t) => t.id === taskId)) return localSprint;
    const fromSnapshot = withSnapshotTask(localSprint);
    if (fromSnapshot) return fromSnapshot;
  }

  if (global && Array.isArray(global.tasks) && global.tasks.some((t) => t.id === taskId)) {
    return global;
  }

  if (isMultiCampaign && localSprint && Array.isArray(localSprint.tasks)) {
    const baseTasks = Array.isArray(global?.tasks) ? global.tasks : [];
    const merged = new Map(baseTasks.map((t) => [t.id, t]));
    localSprint.tasks
      .filter((t) => t.sourceCampaign?.id === sourceSprintId)
      .forEach((t) => merged.set(t.id, t));
    if (snapshotTask) merged.set(taskId, snapshotTask);
    const tasks = [...merged.values()];
    if (tasks.some((t) => t.id === taskId)) {
      return {
        ...(global || { id: sourceSprintId }),
        id: sourceSprintId,
        tasks,
        nodePositions: bridge.sourceNodePositions ?? global?.nodePositions ?? {},
      };
    }
  }

  if (localSprint?.id === sourceSprintId && Array.isArray(localSprint.tasks)) {
    if (localSprint.tasks.some((t) => t.id === taskId)) return localSprint;
    const fromSnapshot = withSnapshotTask(localSprint);
    if (fromSnapshot) return fromSnapshot;
  }

  if (snapshotTask) {
    const fromSnapshot = withSnapshotTask(global || { id: sourceSprintId });
    if (fromSnapshot) return fromSnapshot;
  }

  return global && Array.isArray(global.tasks) ? global : null;
}

function resolveTargetSprintForMove(targetSprintId, inMemorySprints = []) {
  const global = inMemorySprints.find((s) => s.id === targetSprintId);
  if (!global) return null;
  return {
    ...global,
    tasks: Array.isArray(global.tasks) ? global.tasks : [],
  };
}

/** Synchronous optimistic move — returns false if task can't be moved yet */
export function tryOptimisticMoveMissionToCampaign(
  setSprints,
  { taskId, sourceSprintId, targetSprintId },
  inMemorySprints = [],
) {
  if (!taskId || !sourceSprintId || !targetSprintId) return false;
  if (sourceSprintId === targetSprintId) return false;

  const memSource = resolveSourceSprintForMove(sourceSprintId, taskId, inMemorySprints);
  const memTarget = resolveTargetSprintForMove(targetSprintId, inMemorySprints);

  if (!memSource || !memTarget || !Array.isArray(memSource.tasks)) {
    console.warn('[M2S] optimistic move blocked — could not resolve sprints', {
      hasSource: !!memSource,
      sourceHasTasks: Array.isArray(memSource?.tasks),
      sourceTaskCount: memSource?.tasks?.length,
      hasTarget: !!memTarget,
      sourceSprintId,
      targetSprintId,
    });
    return false;
  }

  const moveBundle = buildMoveUpdates(memSource, memTarget, taskId);
  if (!moveBundle) return false;

  // Block the 2s save loop before the optimistic source-without-mission lands.
  holdSprintSaves(sourceSprintId, targetSprintId);
  applyMoveUpdates(setSprints, sourceSprintId, targetSprintId, moveBundle);
  console.log('[M2S] optimistic move applied to app state', {
    taskId,
    sourceSprintId,
    targetSprintId,
    sourceTaskCountAfter: moveBundle.updatedSource.tasks.length,
    targetTaskCountAfter: moveBundle.updatedTarget.tasks.length,
  });
  return true;
}

const applyMoveUpdates = (
  setSprints,
  sourceSprintId,
  targetSprintId,
  { updatedSource, updatedTarget },
) => {
  setSprints((prev) =>
    prev.map((s) => {
      if (s.id === sourceSprintId) return updatedSource;
      if (s.id === targetSprintId) return updatedTarget;
      return s;
    }),
  );

  dispatchTasksUpdatedEvent(
    sourceSprintId,
    updatedSource.tasks,
    updatedSource.nodePositions,
  );
  dispatchTasksUpdatedEvent(
    targetSprintId,
    updatedTarget.tasks,
    updatedTarget.nodePositions,
  );
};

const cloneSprintSnapshot = (sprint) => ({
  ...sprint,
  tasks: (sprint.tasks || []).map((t) => ({ ...t })),
  nodePositions: { ...(sprint.nodePositions || {}) },
});

const revertMoveLocally = (
  setSprints,
  cancelPendingSaveForSprint,
  sourceSprintId,
  targetSprintId,
  previousSource,
  previousTarget,
) => {
  applyMoveUpdates(setSprints, sourceSprintId, targetSprintId, {
    updatedSource: previousSource,
    updatedTarget: previousTarget,
  });
  cancelPendingSaveForSprint?.(sourceSprintId);
  cancelPendingSaveForSprint?.(targetSprintId);
  dispatchCancelSprintSyncDebounce(sourceSprintId);
  dispatchCancelSprintSyncDebounce(targetSprintId);
};

const persistMovePair = (
  setSprints,
  cancelPendingSaveForSprint,
  sourceSprintId,
  targetSprintId,
  { updatedSource, updatedTarget },
  { gainId, loseId, previousGainSprint },
) => {
  holdSprintSaves(sourceSprintId, targetSprintId);
  cancelPendingSaveForSprint?.(sourceSprintId);
  cancelPendingSaveForSprint?.(targetSprintId);
  dispatchCancelSprintSyncDebounce(sourceSprintId);
  dispatchCancelSprintSyncDebounce(targetSprintId);
  applyMoveUpdates(setSprints, sourceSprintId, targetSprintId, { updatedSource, updatedTarget });
  persistMovedMissionPair({
    gainId,
    gainSprint: gainId === targetSprintId ? updatedTarget : updatedSource,
    loseId,
    loseSprint: loseId === sourceSprintId ? updatedSource : updatedTarget,
    previousGainSprint,
  })
    .catch((err) => console.error('[MOVE_MISSION] Undo/redo save failed:', err))
    .finally(() => releaseSprintSaves(sourceSprintId, targetSprintId));
};

/**
 * Move a regular mission task from one campaign to another.
 * Task payload is preserved; flow position uses calculateNextNodePosition on the target.
 */
export const useMoveMissionToCampaign = (setSprints, cancelPendingSaveForSprint) => {
  const moveMissionToCampaign = useCallback(
    async ({ taskId, sourceSprintId, targetSprintId }, inMemorySprints = []) => {
      if (!taskId || !sourceSprintId || !targetSprintId) return false;
      if (sourceSprintId === targetSprintId) return false;

      holdSprintSaves(sourceSprintId, targetSprintId);
      cancelPendingSaveForSprint?.(sourceSprintId);
      cancelPendingSaveForSprint?.(targetSprintId);
      dispatchCancelSprintSyncDebounce(sourceSprintId);
      dispatchCancelSprintSyncDebounce(targetSprintId);

      try {
        const memSource = resolveSourceSprintForMove(sourceSprintId, taskId, inMemorySprints);
        const memTarget = resolveTargetSprintForMove(targetSprintId, inMemorySprints);
        const canOptimistic =
          memSource
          && memTarget
          && Array.isArray(memSource.tasks)
          && Array.isArray(memTarget.tasks);

        let moveBundle = canOptimistic
          ? buildMoveUpdates(memSource, memTarget, taskId)
          : null;

        if (canOptimistic && !moveBundle) return false;

        if (moveBundle) {
          applyMoveUpdates(setSprints, sourceSprintId, targetSprintId, moveBundle);
        }

        // "Fully loaded" = safe to PUT: the backend replaces the whole record and
        // rejects (or would wipe) sprints missing their categories array. In-memory
        // sprints[] often holds metadata-only shells that must never be persisted.
        const isFullyLoaded = (s) => s && Array.isArray(s.tasks) && Array.isArray(s.categories);

        const resolveSprint = async (id, taskIdForSource) => {
          const live = id === sourceSprintId
            ? resolveSourceSprintForMove(id, taskIdForSource, inMemorySprints)
            : resolveTargetSprintForMove(id, inMemorySprints);
          if (isFullyLoaded(live)) return live;

          const mem = inMemorySprints.find((s) => s.id === id);
          if (isFullyLoaded(mem)) return mem;

          const fetched = await SprintAPI.get(id);
          if (!fetched) return live || mem || null;

          if (id === sourceSprintId && live && Array.isArray(live.tasks)) {
            // Source: live view data has the freshest tasks (including the dragged one);
            // take server truth only for the fields the live copy is missing.
            return {
              ...fetched,
              ...live,
              categories: Array.isArray(live.categories) ? live.categories : (fetched.categories || []),
            };
          }

          // Target (or no usable live copy): server truth, normalized for the PUT guard
          return Array.isArray(fetched.categories) ? fetched : { ...fetched, categories: [] };
        };

        let sourceSprint;
        let targetSprint;

        try {
          [sourceSprint, targetSprint] = await Promise.all([
            resolveSprint(sourceSprintId, taskId),
            resolveSprint(targetSprintId, taskId),
          ]);
        } catch (err) {
          console.error('[MOVE_MISSION] Failed to fetch sprint data:', err);
          if (moveBundle) {
            revertMoveLocally(
              setSprints,
              cancelPendingSaveForSprint,
              sourceSprintId,
              targetSprintId,
              moveBundle.previousSource,
              moveBundle.previousTarget,
            );
          }
          return false;
        }

        if (!sourceSprint || !targetSprint) {
          if (moveBundle) {
            revertMoveLocally(
              setSprints,
              cancelPendingSaveForSprint,
              sourceSprintId,
              targetSprintId,
              moveBundle.previousSource,
              moveBundle.previousTarget,
            );
          }
          return false;
        }

        // ALWAYS rebuild the persistence bundle from fully-loaded sprints. The optimistic
        // moveBundle may have been built from metadata-only shells (no categories, empty
        // tasks) — fine for instant UI, but PUTting it gets rejected by the backend's
        // categories guard (or would wipe fields, since PUT replaces the whole record).
        const persistBundle = buildMoveUpdates(sourceSprint, targetSprint, taskId);

        if (!persistBundle) {
          const targetHasTask = (targetSprint.tasks || []).some((t) => t.id === taskId);
          const sourceHasTask = (sourceSprint.tasks || []).some((t) => t.id === taskId);
          if (targetHasTask && !sourceHasTask) {
            // Server already reflects this move (e.g. retry after a partial save)
            console.log('[M2S] move already persisted on server, skipping save', { taskId });
            return true;
          }
          console.warn('[M2S] could not build persistence bundle, reverting', { taskId });
          if (moveBundle) {
            revertMoveLocally(
              setSprints,
              cancelPendingSaveForSprint,
              sourceSprintId,
              targetSprintId,
              moveBundle.previousSource,
              moveBundle.previousTarget,
            );
          }
          return false;
        }

        // Re-apply with full data so global state doesn't keep shell copies around
        applyMoveUpdates(setSprints, sourceSprintId, targetSprintId, persistBundle);

        const { previousSource, previousTarget, updatedSource, updatedTarget } = persistBundle;

        try {
          await persistMovedMissionPair({
            gainId: targetSprintId,
            gainSprint: updatedTarget,
            loseId: sourceSprintId,
            loseSprint: updatedSource,
            previousGainSprint: previousTarget,
          });

          console.log('[M2S] move persisted to API', { taskId, sourceSprintId, targetSprintId });

          const taskName =
            previousSource.tasks?.find((t) => t.id === taskId)?.name || 'Mission';
          const targetName = previousTarget.name || 'campaign';

          const undoSnapshot = {
            updatedSource: cloneSprintSnapshot(previousSource),
            updatedTarget: cloneSprintSnapshot(previousTarget),
          };
          const redoSnapshot = {
            updatedSource: cloneSprintSnapshot(updatedSource),
            updatedTarget: cloneSprintSnapshot(updatedTarget),
          };

          recordUndoableAction({
            type: 'MOVE_MISSION_TO_CAMPAIGN',
            description: `Move: "${taskName}" → ${targetName}`,
            undo: () => {
              persistMovePair(
                setSprints,
                cancelPendingSaveForSprint,
                sourceSprintId,
                targetSprintId,
                undoSnapshot,
                {
                  gainId: sourceSprintId,
                  loseId: targetSprintId,
                  previousGainSprint: redoSnapshot.updatedSource,
                },
              );
            },
            redo: () => {
              persistMovePair(
                setSprints,
                cancelPendingSaveForSprint,
                sourceSprintId,
                targetSprintId,
                redoSnapshot,
                {
                  gainId: targetSprintId,
                  loseId: sourceSprintId,
                  previousGainSprint: undoSnapshot.updatedTarget,
                },
              );
            },
          });

          return true;
        } catch (err) {
          console.error('[MOVE_MISSION] API save failed, reverting:', err);
          revertMoveLocally(
            setSprints,
            cancelPendingSaveForSprint,
            sourceSprintId,
            targetSprintId,
            previousSource,
            previousTarget,
          );
          const destName = previousTarget?.name || 'the destination campaign';
          if (isDynamoItemSizeError(err)) {
            showToast(
              `Couldn't move the mission — "${destName}" is too large to save (400 KB cap). The mission is still in the original campaign.`,
              { type: 'error', duration: 12000 },
            );
          } else {
            showToast(
              `Couldn't move the mission. It's still in the original campaign.`,
              { type: 'error', duration: 8000 },
            );
          }
          return false;
        }
      } finally {
        releaseSprintSaves(sourceSprintId, targetSprintId);
      }
    },
    [setSprints, cancelPendingSaveForSprint],
  );

  return { moveMissionToCampaign };
};

export default useMoveMissionToCampaign;
